import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    Heart, Users, Copy, Check, Send, ArrowRight, 
    Sparkles, Brain, MessageCircle, RefreshCw
} from 'lucide-react';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, SubHeading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PartnerInvite - Send and receive partner invitations
 * 
 * Two views:
 * 1. Create invite (for logged-in users)
 * 2. Accept invite (for invited partners)
 */

// View for creating an invite
export const CreatePartnerInvite = () => {
    const navigate = useNavigate();
    const [inviteData, setInviteData] = useState(null);
    const [partnerName, setPartnerName] = useState('');
    const [personalMessage, setPersonalMessage] = useState('');
    const [creating, setCreating] = useState(false);
    const [copied, setCopied] = useState(false);

    const createInvite = async () => {
        setCreating(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const res = await fetch(`${API_URL}/api/partner/invite/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    partner_name: partnerName || null,
                    personal_message: personalMessage || null
                })
            });

            if (res.ok) {
                const data = await res.json();
                setInviteData(data);
            }
        } catch (error) {
            console.error('Failed to create invite:', error);
        } finally {
            setCreating(false);
        }
    };

    const copyLink = async () => {
        if (inviteData?.invite_link) {
            await navigator.clipboard.writeText(inviteData.invite_link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const shareLink = async () => {
        if (inviteData?.invite_link && navigator.share) {
            try {
                await navigator.share({
                    title: 'Join me on CoRegulate',
                    text: personalMessage || "I'd like you to join me on CoRegulate - an app for ADHD relationships",
                    url: inviteData.invite_link
                });
            } catch (error) {
                // User cancelled or share failed, fallback to copy
                copyLink();
            }
        } else {
            copyLink();
        }
    };

    if (inviteData) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF] p-6">
                <div className="max-w-md mx-auto">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#7D8E95]/30 flex items-center justify-center mb-4">
                            <Check className="w-8 h-8 text-[#1D6B6B]" />
                        </div>
                        <Heading className="text-[#1D6B6B] mb-2">Invite Created!</Heading>
                        <BodyText className="text-[#7D8E95]">
                            Share this link with {partnerName || 'your partner'}
                        </BodyText>
                    </div>

                    <div className="glass-card rounded-2xl p-6 mb-6">
                        <SmallText className="text-[#7D8E95] mb-2">Invite Link</SmallText>
                        <div className="flex items-center gap-2 p-3 bg-white/70 rounded-lg">
                            <input
                                type="text"
                                value={inviteData.invite_link}
                                readOnly
                                className="flex-1 bg-transparent text-sm text-[#1D6B6B] truncate outline-none"
                            />
                            <button
                                onClick={copyLink}
                                className="p-2 hover:bg-[#FFBB98] rounded-lg transition-colors"
                            >
                                {copied ? (
                                    <Check className="w-4 h-4 text-[#1D6B6B]" />
                                ) : (
                                    <Copy className="w-4 h-4 text-[#7D8E95]" />
                                )}
                            </button>
                        </div>
                        
                        <SmallText className="text-[#7D8E95] mt-3">
                            This link expires in 7 days
                        </SmallText>
                    </div>

                    <div className="space-y-3">
                        <button
                            onClick={shareLink}
                            className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#1D6B6B] transition-colors"
                        >
                            <Send className="w-5 h-5" />
                            Share with Partner
                        </button>
                        
                        <button
                            onClick={() => navigate('/home')}
                            className="w-full py-4 px-6 text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF] p-6">
            <div className="max-w-md mx-auto">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#FFBB98]/30 flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-[#FFBB98]" />
                    </div>
                    <Heading className="text-[#1D6B6B] mb-2">Invite Your Partner</Heading>
                    <BodyText className="text-[#7D8E95]">
                        Healing happens together. Invite your partner to join you on CoRegulate.
                    </BodyText>
                </div>

                <div className="glass-card rounded-2xl p-6 mb-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-[#2A8585] mb-1">
                                Partner's Name (optional)
                            </label>
                            <input
                                type="text"
                                value={partnerName}
                                onChange={(e) => setPartnerName(e.target.value)}
                                placeholder="Their name"
                                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-[#2A8585] mb-1">
                                Personal Message (optional)
                            </label>
                            <textarea
                                value={personalMessage}
                                onChange={(e) => setPersonalMessage(e.target.value)}
                                placeholder="Add a personal note to your invite..."
                                rows={3}
                                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30 resize-none"
                            />
                        </div>
                    </div>
                </div>

                {/* What they'll get */}
                <div className="mb-6">
                    <SmallText className="text-[#7D8E95] mb-3">What they'll get access to:</SmallText>
                    <div className="space-y-2">
                        {[
                            { icon: Brain, text: "Understanding how ADHD affects relationships" },
                            { icon: MessageCircle, text: "Communication scripts for hard conversations" },
                            { icon: RefreshCw, text: "Tools to break negative patterns together" },
                            { icon: Heart, text: "Co-regulation exercises to do as a couple" }
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 bg-white/70 rounded-lg">
                                <item.icon className="w-4 h-4 text-[#1D6B6B]" />
                                <SmallText className="text-[#2A8585]">{item.text}</SmallText>
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    onClick={createInvite}
                    disabled={creating}
                    className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#1D6B6B] disabled:opacity-50 transition-colors"
                >
                    {creating ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                        <>
                            <Sparkles className="w-5 h-5" />
                            Create Invite Link
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

// View for receiving/accepting an invite
export const AcceptPartnerInvite = () => {
    const { inviteCode } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [inviteData, setInviteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchInvite = async () => {
            try {
                const res = await fetch(`${API_URL}/api/partner/invite/${inviteCode}`);
                if (res.ok) {
                    const data = await res.json();
                    setInviteData(data);
                } else {
                    setError('Invite not found');
                }
            } catch (err) {
                setError('Failed to load invite');
            } finally {
                setLoading(false);
            }
        };
        fetchInvite();
    }, [inviteCode]);

    const acceptInvite = async () => {
        if (!isAuthenticated) {
            // Store invite code and redirect to auth
            localStorage.setItem('pending_partner_invite', inviteCode);
            navigate('/auth', { state: { from: 'partner-invite', returnTo: `/partner-invite/${inviteCode}` } });
            return;
        }

        setAccepting(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const res = await fetch(`${API_URL}/api/partner/invite/${inviteCode}/accept`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                navigate('/partner-welcome');
            } else {
                const data = await res.json();
                setError(data.detail || 'Failed to accept invite');
            }
        } catch (err) {
            setError('Failed to accept invite');
        } finally {
            setAccepting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D6B6B]"></div>
            </div>
        );
    }

    if (error || inviteData?.status === 'expired' || inviteData?.status === 'accepted') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF] p-6 flex items-center justify-center">
                <div className="max-w-md w-full text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#FFBB98]/30 flex items-center justify-center mb-4">
                        <Heart className="w-8 h-8 text-[#FFBB98]" />
                    </div>
                    <Heading className="text-[#1D6B6B] mb-2">
                        {inviteData?.status === 'expired' ? 'Invite Expired' : 
                         inviteData?.status === 'accepted' ? 'Already Accepted' : 'Invite Not Found'}
                    </Heading>
                    <BodyText className="text-[#7D8E95] mb-6">
                        {inviteData?.message || error || 'This invite is no longer valid.'}
                    </BodyText>
                    <button
                        onClick={() => navigate('/landing')}
                        className="px-6 py-3 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#1D6B6B] transition-colors"
                    >
                        Go to CoRegulate
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF] p-6">
            <div className="max-w-md mx-auto">
                <div className="text-center mb-8">
                    <div className="w-20 h-20 mx-auto rounded-full bg-[#FFBB98]/30 flex items-center justify-center mb-4">
                        <Heart className="w-10 h-10 text-[#FFBB98]" />
                    </div>
                    <Heading className="text-[#1D6B6B] mb-2">You're Invited</Heading>
                    <BodyText className="text-[#7D8E95]">
                        {inviteData?.created_by_name} has invited you to join them on CoRegulate
                    </BodyText>
                </div>

                {inviteData?.personal_message && (
                    <div className="glass-card rounded-2xl p-6 mb-6">
                        <SmallText className="text-[#7D8E95] mb-2">Personal message:</SmallText>
                        <BodyText className="text-[#1D6B6B] italic">
                            "{inviteData.personal_message}"
                        </BodyText>
                    </div>
                )}

                {/* What is CoRegulate */}
                <div className="mb-8">
                    <SubHeading className="text-[#1D6B6B] mb-4 text-center">What is CoRegulate?</SubHeading>
                    <BodyText className="text-[#7D8E95] text-center mb-6">
                        An app designed to help ADHD couples understand each other better, 
                        communicate more effectively, and regulate together.
                    </BodyText>
                    
                    <div className="space-y-3">
                        {[
                            { 
                                icon: Brain, 
                                title: "Understand ADHD's Impact",
                                desc: "Learn how ADHD affects relationships and emotions"
                            },
                            { 
                                icon: MessageCircle, 
                                title: "Better Communication",
                                desc: "Scripts and tools for hard conversations"
                            },
                            { 
                                icon: Users, 
                                title: "Regulate Together",
                                desc: "Co-regulation exercises for when things are hard"
                            }
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-start gap-4 p-4 bg-white/70 rounded-xl">
                                <div className="w-10 h-10 rounded-full bg-[#7D8E95]/30 flex items-center justify-center flex-shrink-0">
                                    <item.icon className="w-5 h-5 text-[#1D6B6B]" />
                                </div>
                                <div>
                                    <BodyText className="font-medium text-[#1D6B6B]">{item.title}</BodyText>
                                    <SmallText className="text-[#7D8E95]">{item.desc}</SmallText>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    onClick={acceptInvite}
                    disabled={accepting}
                    className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#1D6B6B] disabled:opacity-50 transition-colors"
                >
                    {accepting ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                        <>
                            {isAuthenticated ? 'Accept Invitation' : 'Create Account & Join'}
                            <ArrowRight className="w-5 h-5" />
                        </>
                    )}
                </button>
                
                {!isAuthenticated && (
                    <SmallText className="text-center text-[#7D8E95] mt-3">
                        Already have an account? <button onClick={() => navigate('/auth')} className="text-[#1D6B6B] underline">Sign in</button>
                    </SmallText>
                )}
            </div>
        </div>
    );
};

// Welcome page for new partners
export const PartnerWelcome = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF] p-6">
            <div className="max-w-md mx-auto text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#7D8E95]/30 flex items-center justify-center mb-6">
                    <Heart className="w-10 h-10 text-[#1D6B6B]" />
                </div>
                
                <Heading className="text-[#1D6B6B] mb-4">Welcome to CoRegulate</Heading>
                <BodyText className="text-[#7D8E95] mb-8">
                    You're now connected with your partner. This is a space for both of you to 
                    learn, grow, and regulate together.
                </BodyText>

                <div className="glass-card rounded-2xl p-6 mb-8 text-left">
                    <SubHeading className="text-[#1D6B6B] mb-4">A note for you</SubHeading>
                    <BodyText className="text-[#2A8585] mb-4">
                        If your partner has ADHD, you might have experienced:
                    </BodyText>
                    <ul className="space-y-2 mb-4">
                        {[
                            "Feeling like you're the only one managing things",
                            "Repeating the same conversations",
                            "Not understanding why simple things are hard",
                            "Wondering if they really care"
                        ].map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                                <span className="text-[#FFBB98] mt-1">•</span>
                                <SmallText className="text-[#2A8585]">{item}</SmallText>
                            </li>
                        ))}
                    </ul>
                    <BodyText className="text-[#2A8585] italic">
                        Your feelings are valid. This app will help you understand what's 
                        happening and give you tools to work through it together.
                    </BodyText>
                </div>

                <div className="space-y-3">
                    <button
                        onClick={() => navigate('/home')}
                        className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#1D6B6B] transition-colors"
                    >
                        Explore the App
                        <ArrowRight className="w-5 h-5" />
                    </button>
                    
                    <button
                        onClick={() => navigate('/relationship-hub/adhd-relationships')}
                        className="w-full py-4 px-6 text-[#1D6B6B] hover:bg-[#7D8E95]/10 rounded-xl transition-colors"
                    >
                        Start with: Understanding ADHD in Relationships
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreatePartnerInvite;
